<template>
  <div
    ref="containerRef"
    class="pdf-canvas"
    :class="{
      'pdf-canvas--loading': isRendering,
      'pdf-canvas--error': !!error,
    }"
  >
    <!-- Canvas layer for PDF rendering -->
    <canvas
      ref="canvasRef"
      class="pdf-canvas__canvas"
    />

    <!-- Highlight overlay for search results -->
    <canvas
      ref="highlightCanvasRef"
      class="pdf-canvas__highlight-layer"
    />

    <!-- Text layer for selection/search -->
    <div
      v-if="enableTextLayer"
      ref="textLayerRef"
      class="pdf-canvas__text-layer"
    />

    <!-- Loading overlay -->
    <div v-if="isRendering" class="pdf-canvas__loading">
      <v-progress-circular
        indeterminate
        size="32"
        width="3"
        color="primary"
      />
    </div>

    <!-- Error overlay -->
    <div v-if="error" class="pdf-canvas__error">
      <v-icon color="error" size="32" :icon="mdiAlertCircle" />
      <span class="pdf-canvas__error-text">{{ error.message }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted, type PropType } from 'vue'
import { mdiAlertCircle } from '@mdi/js'
import type { PDFPageProxy, RenderTask } from 'pdfjs-dist'
import type { TextContent } from 'pdfjs-dist/types/src/display/api'
import type { PDFSearchResult } from '../types'
import { pdfjsLib } from '../utils/pdfjs-setup'
import { zoomToScale } from '../utils/zoom-utils'

const props = defineProps({
  page: {
    type: Object as PropType<PDFPageProxy | null>,
    default: null,
  },
  zoom: {
    type: Number,
    default: 100,
  },
  enableTextLayer: {
    type: Boolean,
    default: true,
  },
  pixelRatio: {
    type: Number,
    default: () => window.devicePixelRatio || 1,
  },
  renderCache: {
    type: Object as PropType<Map<string, ImageBitmap> | null>,
    default: null,
  },
  searchHighlights: {
    type: Array as PropType<PDFSearchResult[]>,
    default: () => [],
  },
  activeHighlightIndex: {
    type: Number,
    default: -1,
  },
  rotation: {
    type: Number,
    default: 0,
  },
})

const emit = defineEmits<{
  rendered: [dimensions: { width: number; height: number }]
  error: [error: Error]
}>()

// Refs
const containerRef = ref<HTMLDivElement | null>(null)
const canvasRef = ref<HTMLCanvasElement | null>(null)
const highlightCanvasRef = ref<HTMLCanvasElement | null>(null)
const textLayerRef = ref<HTMLDivElement | null>(null)

// State
const isRendering = ref(false)
const error = ref<Error | null>(null)

// Render task reference for cancellation
let currentRenderTask: RenderTask | null = null
let renderCounter = 0

function cacheKey(pageNumber: number, zoom: number): string {
  return `${pageNumber}-${zoom}-${props.rotation}-${window.devicePixelRatio || 1}`
}

/**
 * Renders the current page to canvas, using cache when available
 */
async function renderPage(): Promise<void> {
  const renderId = ++renderCounter

  if (!props.page || !canvasRef.value) {
    return
  }

  // Cancel any ongoing render
  if (currentRenderTask) {
    currentRenderTask.cancel()
    currentRenderTask = null
  }

  const page = props.page
  const canvas = canvasRef.value
  const scale = zoomToScale(props.zoom)
  const viewport = page.getViewport({ scale, rotation: props.rotation })
  const outputScale = window.devicePixelRatio || 1
  const key = cacheKey(page.pageNumber, props.zoom)

  // Set canvas dimensions
  canvas.width = Math.floor(viewport.width * outputScale)
  canvas.height = Math.floor(viewport.height * outputScale)
  canvas.style.width = `${Math.floor(viewport.width)}px`
  canvas.style.height = `${Math.floor(viewport.height)}px`

  // Try cache first
  const cached = props.renderCache?.get(key)
  if (cached) {
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.drawImage(cached, 0, 0)

      if (props.enableTextLayer && textLayerRef.value) {
        await renderTextLayer(page, viewport)
      }

      drawHighlights()
      emit('rendered', { width: viewport.width, height: viewport.height })
      return
    }
  }

  // Full pdf.js render
  isRendering.value = true
  error.value = null

  try {
    const ctx = canvas.getContext('2d')

    if (!ctx) {
      throw new Error('Could not get canvas context')
    }

    const transform: [number, number, number, number, number, number] | null =
      outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null

    const renderContext = {
      canvasContext: ctx,
      transform,
      viewport,
    }

    currentRenderTask = page.render(renderContext)
    await currentRenderTask.promise

    // Store in cache
    if (props.renderCache) {
      try {
        const bitmap = await createImageBitmap(canvas)
        props.renderCache.set(key, bitmap)
      } catch {
        // Cache storage is best-effort
      }
    }

    // Render text layer if enabled
    if (props.enableTextLayer && textLayerRef.value) {
      await renderTextLayer(page, viewport)
    }

    drawHighlights()

    emit('rendered', {
      width: viewport.width,
      height: viewport.height,
    })
  } catch (err) {
    if (err instanceof Error && err.name === 'RenderingCancelledException') {
      return
    }

    const renderError = err instanceof Error ? err : new Error('Failed to render page')
    error.value = renderError
    emit('error', renderError)
  } finally {
    isRendering.value = false
    currentRenderTask = null
  }
}

/**
 * Renders the text layer for text selection and search
 */
async function renderTextLayer(page: PDFPageProxy, viewport: ReturnType<PDFPageProxy['getViewport']>): Promise<void> {
  const textLayerDiv = textLayerRef.value
  if (!textLayerDiv) return

  textLayerDiv.innerHTML = ''
  textLayerDiv.style.width = `${viewport.width}px`
  textLayerDiv.style.height = `${viewport.height}px`

  try {
    const textContent: TextContent = await page.getTextContent()

    const textLayer = new pdfjsLib.TextLayer({
      textContentSource: textContent,
      container: textLayerDiv as HTMLDivElement,
      viewport,
    })

    await textLayer.render()
  } catch {
    // Text layer rendering is optional
  }
}

/**
 * Draws search result highlights on the overlay canvas
 */
function drawHighlights(): void {
  const highlightCanvas = highlightCanvasRef.value
  if (!highlightCanvas || !props.page) return

  const ctx = highlightCanvas.getContext('2d')
  if (!ctx) return

  const scale = zoomToScale(props.zoom)
  const viewport = props.page.getViewport({ scale, rotation: props.rotation })
  const dpr = window.devicePixelRatio || 1

  highlightCanvas.width = Math.floor(viewport.width * dpr)
  highlightCanvas.height = Math.floor(viewport.height * dpr)
  highlightCanvas.style.width = `${Math.floor(viewport.width)}px`
  highlightCanvas.style.height = `${Math.floor(viewport.height)}px`

  ctx.clearRect(0, 0, highlightCanvas.width, highlightCanvas.height)

  if (!props.searchHighlights.length) return

  ctx.save()
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

  for (const highlight of props.searchHighlights) {
    // Full affine matrix composition via pdf.js — handles scale, rotation, and y-axis flip
    const tx = pdfjsLib.Util.transform(viewport.transform, highlight.transform)
    const x = tx[4]
    const y = tx[5]
    const w = highlight.width * scale
    const h = highlight.height * scale

    const isActive = highlight.matchIndex === props.activeHighlightIndex

    ctx.fillStyle = isActive
      ? 'rgba(255, 165, 0, 0.5)'
      : 'rgba(255, 255, 0, 0.4)'

    ctx.save()
    ctx.translate(x, y)
    ctx.rotate((props.rotation * Math.PI) / 180)
    ctx.fillRect(0, -h, w, h)
    ctx.restore()
  }

  ctx.restore()
}

// Watch for page, zoom, or rotation changes
watch(
  () => [props.page, props.zoom, props.rotation],
  () => {
    renderPage()
  },
)

// Redraw highlights when search state changes (without re-rendering the page)
watch(
  () => [props.searchHighlights, props.activeHighlightIndex],
  () => {
    drawHighlights()
  },
)

// Render once the canvas DOM element is available
onMounted(() => {
  renderPage()
})

// Cancel render on unmount
onUnmounted(() => {
  if (currentRenderTask) {
    currentRenderTask.cancel()
    currentRenderTask = null
  }
})
</script>

<style lang="scss" scoped>
@use '@/styles/design-tokens' as *;

.pdf-canvas {
  position: relative;
  display: inline-block;
  background: white;
  box-shadow: $shadow-sm;
  border-radius: $radius-sm;
  overflow: hidden;
  @include transition-normal(box-shadow);

  &:hover {
    box-shadow: $shadow-md;
  }

  &__canvas {
    display: block;
  }

  &__highlight-layer {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }

  &__text-layer {
    position: absolute;
    top: 0;
    left: 0;
    overflow: hidden;
    opacity: 0.2;
    line-height: 1;

    :deep(span) {
      position: absolute;
      white-space: pre;
      color: transparent;
      pointer-events: all;

      &::selection {
        background: rgb(var(--v-theme-primary));
        color: transparent;
      }
    }
  }

  &__loading {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.8);
    @include transition-fast(opacity);
  }

  &__error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: $space-sm;
    background: rgba(255, 255, 255, 0.95);
  }

  &__error-text {
    font-size: $text-sm;
    color: rgb(var(--v-theme-error));
    text-align: center;
    max-width: 200px;
  }

  @include reduced-motion {
    transition: none;

    &:hover {
      box-shadow: $shadow-sm;
    }

    .pdf-canvas__loading {
      transition: none;
    }
  }
}
</style>
